<script lang="ts">
	import { getAuthContext } from '../NewsHeader/AuthDialogs/utils/getAuthContext/getAuthContext';
	import { SavedTagsBar } from './SavedTagsBar';
	import { page } from '$app/state';

	interface Props {
		forceShowGlobalNav?: boolean | undefined;
	}

	let { forceShowGlobalNav = undefined }: Props = $props();

	let defaultTags = $derived(page.data.defaultTags);

	const {
		user,
		usersPrefs,
		removeUserTag,
		hasLoadedUserPrefs,
		isRestoringSession,
	} = getAuthContext();

	const getHandleClickCross =
		(userPref: Parameters<typeof removeUserTag>[0]) => () => {
			if (userPref?.id) {
				removeUserTag(userPref);
			}
		};

	// Read here rather than inside the branch that renders them: `usersPrefs` is
	// a lazy derived, and reading it only from inside `{#if showUserTags}` means
	// nothing subscribes while the bar is withheld. createAuthContext now holds
	// its own subscription so that can no longer deadlock, but keeping the read
	// unconditional stops this component from depending on that detail.
	let savedTags = $derived($usersPrefs.savedTags);

	// Signed-out visitors get the bar immediately — `defaultTags` comes from the
	// /news layout load, so it is in `page.data` from the first server render and
	// never needs to wait on Cognito.
	//
	// A visitor with a stored session is the one case we hold it back: showing
	// defaults there would mean flashing tags that are about to be replaced by
	// their own. `isRestoringSession` is known synchronously at mount, so the bar
	// is withheld from the first paint rather than appearing and then swapping.
	let showUserTags = $derived($hasLoadedUserPrefs && !!$user);
	let showBar = $derived(showUserTags || !$isRestoringSession);
</script>

{#if showBar}
	<div class="saved-tags-mount">
		{#if showUserTags}
			<SavedTagsBar
				tags={savedTags}
				{getHandleClickCross}
				{forceShowGlobalNav}
			/>
		{:else}
			<SavedTagsBar tags={defaultTags} {forceShowGlobalNav} />
		{/if}
	</div>
{/if}

<style>
	/* Pre-hydration twin of the isRestoringSession gate. The attribute is
	stamped on <html> by the pre-paint probe in NewsLayout when a Cognito
	session is stored (NEWS_RESTORING_SESSION_ATTR — keep this selector in sync)
	and removed by createAuthContext once the restore settles. Without it the
	server-rendered default tags are painted until hydration, flashing them at
	signed-in visitors. The wrapper is layout-neutral: everything inside is
	position: fixed. */
	:global(html[data-news-restoring-session]) .saved-tags-mount {
		display: none;
	}
</style>
